/**
 * System events that can be emitted by workflows
 * These events can trigger other workflows or be consumed by external systems
 */
export enum SystemEvent {
  // Email ingestion events
  EMAIL_INGESTED = "EMAIL_INGESTED",
  EMAIL_ROUTED_EMAIL_RULE = "EMAIL_ROUTED_EMAIL_RULE",
  EMAIL_ROUTED_AR_REMITTANCE = "EMAIL_ROUTED_AR_REMITTANCE",

  // AP invoice events
  AP_INVOICE_ACCEPTED = "AP_INVOICE_ACCEPTED",

  // Task events
  TASK_CREATED = "TASK_CREATED",
  // Emitted when any task is completed. Counterpart to TASK_CREATED; carries the
  // task category + linked entity + result so a workflow can re-trigger off it
  // (e.g. OMS v3 workflow-3 re-runs when a sales-order approval/action task is
  // completed). Single re-trigger event for both approvals and corrective actions.
  TASK_COMPLETED = "TASK_COMPLETED",

  // AR sales order events
  SALES_ORDER_APPROVED = "SALES_ORDER_APPROVED",

  // Procurement: a draft purchase order was created (buyer Confirm) and held for
  // role/$-authority approval. Triggers the per-division PO approval workflow,
  // which gates on the PO amount + initiator and releases the held draft to the
  // outbound push on approval. See docs/procurement/po-approval-workflow.md.
  DRAFT_PURCHASE_ORDER_CREATED = "DRAFT_PURCHASE_ORDER_CREATED",

  // OMS v3 draft-sales-order routing/handoff events
  // Routing org → target org (cross-org): the routing workflow classified the
  // target org and hands the draft off (carries targetOrganizationId).
  DRAFT_SALES_ORDER_ROUTED = "DRAFT_SALES_ORDER_ROUTED",
  // Target org, intra-org: the target org's extract/line-breakout workflow has
  // populated the draft and it is ready for the approval-chain workflow.
  DRAFT_SALES_ORDER_READY = "DRAFT_SALES_ORDER_READY",
  // Routing org: classify_organization could not confidently match a target org;
  // emitted so an admin-notification workflow can alert the Gently team to
  // manually assign (→ reassign_draft_sales_order).
  DRAFT_SALES_ORDER_UNCLASSIFIED = "DRAFT_SALES_ORDER_UNCLASSIFIED",

  // AR approval events (discount and order-level approvals)
  APPROVAL_NEEDED = "APPROVAL_NEEDED",

  // Discounts v2: a discount group (exception price list / bulk batch /
  // discount request) was submitted for approval. Triggers the group-approval
  // workflow, whose Approval step decides it and flips the group's elements
  // active on approve.
  DISCOUNT_GROUP_SUBMITTED = "DISCOUNT_GROUP_SUBMITTED",

  // Discounts v2, routing org → target org (cross-org): the routing workflow
  // classified an inbound email's document(s) as a Discount Request and
  // matched the selling org; hands the email off so the target org's
  // event-triggered discount-request-ingestion workflow extracts it and
  // creates the discount group there (carries targetOrganizationId).
  DISCOUNT_REQUEST_DOCUMENT_ROUTED = "DISCOUNT_REQUEST_DOCUMENT_ROUTED",

  // Rebate CSV transform (workflow started via API with session + artifact context)
  REBATE_CSV_TRANSFORM_STARTED = "REBATE_CSV_TRANSFORM_STARTED",

  // Pricing SPA request entity created (Module 1 CRUD — triggers the workflow DAG)
  SPA_REQUEST_SUBMITTED = "SPA_REQUEST_SUBMITTED",

  // Slack webhook received (Events API callbacks, interactivity payloads,
  // slash commands). Only emitted when the server has Slack credentials
  // configured AND the org has installed the Slack integration; hidden
  // from trigger pickers otherwise.
  SLACK_WEBHOOK_RECEIVED = "SLACK_WEBHOOK_RECEIVED",
}
